// @ts-nocheck
/* eslint-disable guard-for-in, camelcase, no-restricted-syntax, no-param-reassign */
import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';
import { create } from 'zustand';
import { API_URL } from '../configs';
import getRoot from './root';

const defaultFilters = { domain: null, path: null, timeSpanInMonths: 1 };

let emailList = [];
let emailRef = null;

const useAnalyticsStore = create((set, get) => ({
  loading: {
    setTimeSpan: false,
    getRawAnalytics: false,
    getInfluencedSaves: false,
    initiateEmailDownload: false,
    getCaptureList: false,
    getGenerateAnalytics: false,
    getEmailEvents: false,
    getRecoveredForms: false,
    analyticsData: false,
  },
  analyticsData: {},
  emailEvents: [],
  emailEventsPagination: {
    page: 1, pageSize: 50, total: 0, totalPages: 0,
  },
  recoveredForms: [],
  recoveredFormsPagination: {
    page: 1, pageSize: 50, total: 0, totalPages: 0,
  },
  percentView: false,
  filters: defaultFilters,
  rawAnalytics: {},
  influencedAnalyticsData: {},
  // Kept as an action rather than a get/set accessor pair: zustand's set()
  // spreads state into a new object, which would freeze an accessor into a
  // plain data property after the first update.
  setEmailRef: (el) => { emailRef = el; },

  reset: () => set({
    rawAnalytics: {},
    filters: defaultFilters,
    influencedAnalyticsData: {},
    loading: _.mapValues(get().loading, () => false),
    analyticsData: {},
    emailEvents: [],
    emailEventsPagination: {
      page: 1, pageSize: 50, total: 0, totalPages: 0,
    },
    recoveredForms: [],
    recoveredFormsPagination: {
      page: 1, pageSize: 50, total: 0, totalPages: 0,
    },
  }),

  setTimeSpan: async (_e, { value }) => {
    set((s) => ({
      loading: { ...s.loading, setTimeSpan: true },
      filters: { ...s.filters, timeSpanInMonths: value },
    }));
    try {
      await Promise.all([get().getRawAnalytics(), get().getInfluencedSaves()]);
    } finally {
      set((s) => ({ loading: { ...s.loading, setTimeSpan: false } }));
    }
  },

  setFilters: (next) => set((s) => ({ filters: { ...s.filters, ...next } })),

  getRawAnalytics: async () => {
    set((s) => ({ loading: { ...s.loading, getRawAnalytics: true } }));
    try {
      const { filters, rawAnalytics } = get();
      if (!rawAnalytics[filters.timeSpanInMonths]) {
        const { data } = await axios.post(`${API_URL}/api/analytics/stats`, {
          dateStart: moment().subtract(filters.timeSpanInMonths, 'month').format('YYYY-MM-DD'),
          dateEnd: moment().format('YYYY-MM-DD'),
        });
        const { rows = [] } = data;
        const byDate = rows.reduce((sortedData, row) => {
          const { domain, path, date_start: dateStart } = row;
          _.set(sortedData, [domain, path, dateStart], row);
          return sortedData;
        }, {});
        set((s) => ({ rawAnalytics: { ...s.rawAnalytics, [filters.timeSpanInMonths]: byDate } }));
      }
    } catch (e) {
      console.error(e);
      getRoot().failure('Failed to load analytics data.');
    } finally {
      set((s) => ({ loading: { ...s.loading, getRawAnalytics: false } }));
    }
  },

  filteredAnalytics: () => {
    const { rawAnalytics, filters } = get();
    const { domain: domainFilter, path: pathFilter } = filters;
    let filtered = rawAnalytics[filters.timeSpanInMonths] || {};
    if (domainFilter) filtered = _.pick(filtered, domainFilter);
    if (pathFilter && domainFilter) {
      filtered = { ...filtered };
      filtered[domainFilter] = _.pick(filtered[domainFilter] || {}, pathFilter);
    }
    return filtered;
  },

  chartData: () => {
    const chartKeys = ['forms_started', 'abandons', 'unique_valid_abandoned_emails', 'recaptures'];
    const filtered = get().filteredAnalytics();
    const sums = _.reduce(filtered, (acc, domainNode) => {
      _.forEach(domainNode, (pathNode) => {
        _.forEach(pathNode, (dateData, dateStart) => {
          if (acc[dateStart]) {
            chartKeys.forEach((key) => { acc[dateStart][key] += Number(dateData[key]) || 0; });
          } else {
            acc[dateStart] = _.clone(dateData);
            chartKeys.forEach((key) => { acc[dateStart][key] = Number(dateData[key]) || 0; });
          }
        });
      });
      return acc;
    }, {});
    return Object.entries(sums).sort(([a], [b]) => moment(a).diff(moment(b)));
  },

  statSum: (statKey) => _.reduce(get().filteredAnalytics(), (sum, domainNode) => {
    _.forEach(domainNode, (pathNode) => {
      _.forEach(pathNode, (rowData) => { sum += Number(rowData[statKey]) || 0; });
    });
    return sum;
  }, 0),

  createSummaryRows: (statKeys) => {
    const rows = [];
    _.forEach(get().filteredAnalytics(), (domainNode, domain) => {
      _.forEach(domainNode, (pathNode, path) => {
        const url = `${domain}${path}`;
        const statSums = statKeys.map((statKey) => _.reduce(
          pathNode, (sum, dateData) => sum + (Number(dateData[statKey]) || 0), 0,
        ));
        rows.push([url, ...statSums]);
      });
    });
    return rows;
  },

  getInfluencedSaves: async () => {
    const { filters } = get();
    try {
      if (!get().influencedAnalyticsData[filters.timeSpanInMonths]) {
        set((s) => ({ loading: { ...s.loading, getInfluencedSaves: true } }));
        const res = await axios.post(`${API_URL}/api/reporting/retrieve-influenced-saves`, {
          dateStart: moment().subtract(filters.timeSpanInMonths, 'month').format('YYYY-MM-DD'),
          dateEnd: moment().format('YYYY-MM-DD'),
        });
        if (res.data.success) {
          set((s) => ({ influencedAnalyticsData: {
            ...s.influencedAnalyticsData,
            [filters.timeSpanInMonths]: res.data.influencedSaves,
          } }));
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      set((s) => ({ loading: { ...s.loading, getInfluencedSaves: false } }));
    }
  },

  totalInfluencedSaved: () => {
    const { influencedAnalyticsData, filters } = get();
    const data = influencedAnalyticsData[filters.timeSpanInMonths] || [];
    return data
      .filter((item) => {
        if (filters.domain === null) return true;
        if (filters.path !== null) {
          return filters.domain === item.host && filters.path === item.path;
        }
        return filters.domain === item.host;
      })
      .reduce((agg, item) => agg + (Number(item.influenced_saves) || 0), 0);
  },

  totalDirectRecaptures: () => _.reduce(get().chartData(), (sum, [, data]) => sum + (Number(data.recaptures) || 0), 0),

  totalSaves: () => get().totalDirectRecaptures() + get().totalInfluencedSaved(),

  getCaptureList: async (
    startDate = moment().subtract('1', 'month').format('YYYY/MM/DD'),
    endDate = moment().format('YYYY/MM/DD'),
  ) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, getCaptureList: true } }));
    try {
      const { data } = await axios.post(`${API_URL}/api/reporting/retrieve-leads`, { startDate, endDate });
      if (data.success) {
        const { captures } = data;
        const headers = ['Email', 'Capture Url', 'Captured Date', 'Abandoned'];
        const tempList = [];
        for (const capture of captures) {
          const row = new Array(headers.length).fill('');
          row[0] = capture.email;
          row[1] = capture.url;
          row[2] = capture.created_ts;
          row[3] = capture.abandoned;
          const { form_data } = capture;
          for (const column in form_data) {
            if (headers.indexOf(column) === -1) {
              headers.push(column);
              row.push(form_data[column]);
            } else {
              row[headers.indexOf(column)] = form_data[column];
            }
          }
          tempList.push(row.map((val) => `"${_.escape(val)}"`));
        }
        for (const row in tempList) {
          while (tempList[row].length < headers.length) tempList[row].push('""');
        }
        tempList.unshift(headers.map((h) => `"${h}"`));
        emailList = tempList;
      }
    } catch (err) {
      root.failure('Failed to download CSV!');
    }
    set((s) => ({ loading: { ...s.loading, getCaptureList: false } }));
  },

  downloadCaptures: async (startDate, endDate) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, initiateEmailDownload: true } }));
    let csvString = '';

    await get().getCaptureList(startDate, moment(endDate).add(1, 'day').toDate());
    if (_.isEmpty(emailList)) {
      root.failure('Failed to download Captures CSV!');
    }
    for (const rowIndex in emailList) csvString += `${emailList[rowIndex].join(',')}\n`;

    if (emailRef) {
      emailRef.href = URL.createObjectURL(new Blob([csvString], { type: 'text/csv' }));
      emailRef.download = `${moment(startDate).format('MM/DD/YYYY')}-${moment(endDate).format('MM/DD/YYYY')}_abandoned_emails.csv`;
      emailRef.click();
    }
    set((s) => ({ loading: { ...s.loading, initiateEmailDownload: false } }));
  },

  generateAnalytics: async () => {
    try {
      set((s) => ({ loading: { ...s.loading, getGenerateAnalytics: true } }));
      const res = await axios.post(`${API_URL}/api/reporting/analytics-domain`, {
        dateStart: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        dateEnd: moment().format('YYYY-MM-DD'),
      });
      if (res.data.success) set({ analyticsData: res.data.data });
    } catch (e) {
      console.error(e);
    } finally {
      set((s) => ({ loading: { ...s.loading, getGenerateAnalytics: false } }));
    }
  },

  togglePercentView: () => set((s) => ({ percentView: !s.percentView })),

  getEmailEvents: async ({
    startDate,
    endDate,
    eventTypes,
    page = 1,
    pageSize = 50,
  } = {}) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, getEmailEvents: true } }));
    try {
      const { data } = await axios.post(`${API_URL}/api/reporting/retrieve-email-events`, {
        startDate: startDate && moment(startDate).format('YYYY-MM-DD'),
        endDate: endDate && moment(endDate).format('YYYY-MM-DD'),
        eventTypes,
        page,
        pageSize,
      });
      if (data.success) {
        set({ emailEvents: data.events, emailEventsPagination: data.pagination });
      }
    } catch (e) {
      console.error(e);
      root.failure('Failed to load email activity.');
    } finally {
      set((s) => ({ loading: { ...s.loading, getEmailEvents: false } }));
    }
  },

  getRecoveredForms: async ({
    startDate,
    endDate,
    page = 1,
    pageSize = 50,
  } = {}) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, getRecoveredForms: true } }));
    try {
      const { data } = await axios.post(`${API_URL}/api/reporting/retrieve-recovered-forms`, {
        startDate: startDate && moment(startDate).format('YYYY-MM-DD'),
        endDate: endDate && moment(endDate).format('YYYY-MM-DD'),
        page,
        pageSize,
      });
      if (data.success) {
        set({ recoveredForms: data.recoveredForms, recoveredFormsPagination: data.pagination });
      }
    } catch (e) {
      console.error(e);
      root.failure('Failed to load recovered forms.');
    } finally {
      set((s) => ({ loading: { ...s.loading, getRecoveredForms: false } }));
    }
  },
}));

export default useAnalyticsStore;
